'use client';

import React from 'react';
import Image from 'next/image';
import { X } from 'lucide-react';
import GoogleTranslateWidget from '@/components/GoogleTranslateWidget';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const COLORS = {
  navy: '#1F1D40',
  crimson: '#D41F4C',
  cream: '#FAF8F6',
  border: '#E8E4DF',
  muted: '#8A8598',
  card: '#FFFFFF',
} as const;

const POPIN_VISUAL = '/assets/popin/2.png';

const STEPS = [
  {
    number: 1,
    title: 'Pour tous vos projets',
    body: 'Tourisme, Événement, Automobile, Immobilier, Commerce…',
  },
  {
    number: 2,
    title: 'Filmez ou importez vos vidéos',
    body: "L'application vous guide pour réaliser les meilleurs plans.",
  },
  {
    number: 3,
    title: 'Réceptionnez votre mini-film',
    body: 'Votre vidéo est automatiquement montée et prête à être partagée.',
  },
] as const;

const PERSONALIZE = ['Votre style', 'En musique', 'Crédits personnalisés'] as const;

type Props = {
  open: boolean;
  onClose: () => void;
  primaryLabel?: string;
  onPrimary?: () => void;
};

export default function MagicMovieOnboardingPopin({
  open,
  onClose,
  primaryLabel = 'Créer mon Magic Movie',
  onPrimary,
}: Props) {
  if (!open) return null;

  const handlePrimary = () => {
    if (onPrimary) onPrimary();
    else onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[10050] flex flex-col px-[max(0.35rem,env(safe-area-inset-left))] pr-[max(0.35rem,env(safe-area-inset-right))] pt-[max(0.35rem,env(safe-area-inset-top))] pb-[max(0.35rem,env(safe-area-inset-bottom))] sm:px-2 sm:py-2"
      style={{ backgroundColor: 'rgba(31,29,64,0.72)', backdropFilter: 'blur(6px)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="magic-movie-popin-tagline"
    >
      <div
        className="relative flex flex-col flex-1 min-h-0 w-full max-w-none mx-auto rounded-[20px] sm:rounded-[24px] shadow-2xl overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <GoogleTranslateWidget />
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 z-10 max-w-[calc(100%-5rem)]"
          aria-label="Traduction"
        >
          <LanguageSwitcher variant="desktop" />
        </div>
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: COLORS.cream, color: COLORS.muted, border: `1px solid ${COLORS.border}` }}
          aria-label="Fermer"
        >
          <X size={18} />
        </button>

        <div className="flex flex-col flex-1 min-h-0 px-5 sm:px-10 pt-14 sm:pt-16 pb-4 sm:pb-5">
          <div className="flex-1 min-h-0 flex items-center justify-center">
            <div className="text-center w-full max-w-sm mx-auto">
              <p
                id="magic-movie-popin-tagline"
                className="text-xs sm:text-sm font-medium mb-0.5"
                style={{ color: COLORS.muted, fontFamily: "'Work Sans', sans-serif" }}
              >
                Votre film prêt en quelques minutes
              </p>
              <p
                className="text-[13px] sm:text-sm leading-snug"
                style={{ color: COLORS.navy, fontFamily: "'Work Sans', sans-serif" }}
              >
                Filmez ou importez vos vidéos,
                <br />
                nous nous occupons du montage.
              </p>
            </div>
          </div>

          <div className="shrink-0 flex flex-col w-full gap-5 sm:gap-7">
            <div className="relative mx-auto w-full max-w-[min(320px,84vw)] sm:max-w-[360px] h-[min(30vh,240px)] sm:h-[min(34vh,280px)]">
              <Image
                src={POPIN_VISUAL}
                alt=""
                fill
                className="object-contain object-center"
                sizes="360px"
                priority
              />
            </div>

            <div className="w-full">
              <div className="grid grid-cols-3 gap-2 sm:gap-4 justify-items-center">
                {STEPS.map((step) => (
                  <article
                    key={step.number}
                    className="flex flex-col items-center text-center w-full min-w-0"
                  >
                    <div
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold mb-1 sm:mb-2 shrink-0"
                      style={{ backgroundColor: COLORS.crimson, color: '#FFFFFF' }}
                    >
                      {step.number}
                    </div>
                    <h3
                      className="text-[10px] sm:text-sm font-bold leading-tight mb-0.5 px-0.5"
                      style={{ color: COLORS.navy, fontFamily: "'Abel', sans-serif" }}
                    >
                      {step.title}
                    </h3>
                    <p
                      className="text-[9px] sm:text-[11px] leading-snug px-0.5 sm:line-clamp-none"
                      style={{ color: COLORS.muted, fontFamily: "'Work Sans', sans-serif" }}
                    >
                      {step.body}
                    </p>
                  </article>
                ))}
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 flex flex-col items-center justify-center text-center py-2 sm:py-3">
            <p
              className="text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.18em] mb-2"
              style={{ color: COLORS.muted, fontFamily: "'Acme', 'Work Sans', sans-serif" }}
            >
              Personnalisez votre film
            </p>
            <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
              {PERSONALIZE.map((label) => (
                <span
                  key={label}
                  className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold"
                  style={{
                    backgroundColor: COLORS.cream,
                    color: COLORS.navy,
                    border: `1px solid ${COLORS.border}`,
                    fontFamily: "'Work Sans', sans-serif",
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={handlePrimary}
            className="shrink-0 w-full py-3 sm:py-3.5 rounded-2xl text-sm font-bold text-white shadow-lg transition-opacity hover:opacity-95"
            style={{ backgroundColor: COLORS.crimson, fontFamily: "'Acme', sans-serif" }}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
